import { DEBOUNCE_TIME_300 } from "@Configs/consts";
import { useDebounceFn } from "ahooks";
import type { MenuProps } from "antd";
import {
  PaginationProps as AntdPaginationProps,
  Dropdown,
  Pagination as PaginationAntd,
} from "antd";
import { ArrowDown, Checked } from "assets/icons";
import { TFunction } from "i18next";
import React from "react";
import "./Pagination.scss";

export interface PaginationProps extends AntdPaginationProps {
  /**Current page is skipped */
  pageIndex?: number;
  /**Number of records to get in current page */
  pageSize?: number;
  /**Used to custom the page size options */
  pageSizeOptions?: number[];
  /**Function to change current page */
  onChange?: (pageIndex: number, pageSize: number) => void;
  /**Enter page number to go */
  // enableEnterPageNumber?: boolean;
  /**Show change page size */
  changePageSize?: boolean;
  showCurrentRecordsNumber?: boolean;
  currentRecordNumberPosition?: "left" | "right";
   /**Provide a translate function*/
    translate?: TFunction;
}

function Pagination(props: PaginationProps) {
  const {
    pageIndex = 1,
    pageSize = 10,
    total = 100,
    onChange,
    pageSizeOptions = [10, 20, 30, 40, 50],
    changePageSize = true,
    showCurrentRecordsNumber = true,
    currentRecordNumberPosition = "right",
    translate
  } = props;
  
  const [pageSizeSelected, setPageSizeSelected] = React.useState<number>(
    pageSize ? pageSize : pageSizeOptions?.[0] ? pageSizeOptions?.[0] : 10
  );
  const [currentPage, setCurrentPage] = React.useState<number>(1);

  React.useEffect(() => {
    setCurrentPage(pageIndex);
  }, [pageIndex]);

  const { run } = useDebounceFn(
    (nextPage: number) => {
      setCurrentPage(nextPage);
      onChange(nextPage, pageSizeSelected);
    },
    {
      wait: DEBOUNCE_TIME_300,
    }
  );
  const handleChangeCurrentPage = React.useCallback(
    (nextPage: number) => {
      run(nextPage);
    },
    [run]
  );

  const handleMenuTakeClick = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      setPageSizeSelected(Number(event.key));
      onChange(1, Number(event.key));
    },
    [onChange]
  );

  const menuPageSize: MenuProps = React.useMemo(() => {
    return {
      onClick: handleMenuTakeClick,
      className: "menu-page-size",
      selectedKeys: ["" + pageSizeSelected],
      items: pageSizeOptions.map((page) => {
        return {
          key: page,
          label: (
            <div className="page-size-item">
              {page} <Checked />
            </div>
          ),
        };
      }),
    };
  }, [pageSizeOptions, handleMenuTakeClick, pageSizeSelected]);

  return (
    <div className="pagination-container">
      <div className="pagination-left ">
        {changePageSize ? (
          <div className="pagination-page-size">
            <span>{translate("CM.pagination.recordPerPageTitle")}</span>
            <div>
              <Dropdown
                className="dropdown-pagination-per-page"
                menu={menuPageSize}
                trigger={["click"]}
                placement="topRight"
              >
                <button className="page-size-button">
                  {pageSizeSelected}
                  <ArrowDown fillColor="#7A869A" />
                </button>
              </Dropdown>
            </div>
          </div>
        ) : (
          <></>
        )}
        {showCurrentRecordsNumber && currentRecordNumberPosition === "left" ? (
          <div className="current-skip-take-records__left">
            {total ? (((pageIndex - 1) * pageSizeSelected) + 1) : 0}-{pageIndex * pageSizeSelected > total ? total : pageIndex * pageSizeSelected} {translate("CM.pagination.of")} {total} {translate("CM.pagination.records")}
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="pagination-right">
        {showCurrentRecordsNumber && currentRecordNumberPosition === "right" ? (
          <div className="current-skip-take-records__right">
            {total ? (((pageIndex - 1) * pageSizeSelected) + 1) : 0}-{pageIndex * pageSizeSelected > total ? total : pageIndex * pageSizeSelected} {translate("CM.pagination.of")} {total} {translate("CM.pagination.records")}
          </div>
        ) : (
          <></>
        )}
        <PaginationAntd
          current={currentPage}
          pageSize={pageSizeSelected}
          total={total}
          onChange={handleChangeCurrentPage}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}
export default Pagination;
